import { Project, Brand, Product, KeywordWithIntent, KeywordIntent } from '../types';

// Helper function to convert string arrays to KeywordWithIntent
const convertKeywords = (keywords: string[], defaultIntent: KeywordIntent = 'sensory-benefit'): KeywordWithIntent[] => {
  return keywords.map(keyword => ({
    text: keyword,
    intent: defaultIntent,
    volume: Math.floor(Math.random() * (100000 - 0 + 1)) + 0 // Random volume between 0-100,000
  }));
};

// Helper function to generate realistic volume data
const generateVolume = (baseVolume: number, variance: number = 0.3): number => {
  const min = Math.max(0, baseVolume * (1 - variance));
  const max = Math.min(100000, baseVolume * (1 + variance));
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const sampleBrands: Brand[] = [
  {
    id: '1',
    name: 'TechCorp',
    color: '#3b82f6',
  },
  {
    id: '2',
    name: 'FashionForward',
    color: '#ec4899',
  },
  {
    id: '3',
    name: 'HomeEssentials',
    color: '#10b981',
  },
  {
    id: '4',
    name: 'SportsPro',
    color: '#f59e0b',
  },
];

export const sampleProducts: Product[] = [
  {
    id: '1',
    name: 'Wireless Bluetooth Headphones',
    description: 'Premium noise-canceling wireless headphones with 30-hour battery life',
    category: 'Electronics',
    keywords: [
      // Category Anchor
      { text: 'headphones', intent: 'category-anchor', volume: generateVolume(15000) },
      { text: 'earphones', intent: 'category-anchor', volume: generateVolume(12000) },
      { text: 'earbuds', intent: 'category-anchor', volume: generateVolume(8000) },
      { text: 'headset', intent: 'category-anchor', volume: generateVolume(6000) },
      { text: 'audio', intent: 'category-anchor', volume: generateVolume(10000) },
      
      // Preparation / Format
      { text: 'wireless', intent: 'preparation-format', volume: generateVolume(18000) },
      { text: 'bluetooth', intent: 'preparation-format', volume: generateVolume(16000) },
      { text: 'cordless', intent: 'preparation-format', volume: generateVolume(4000) },
      { text: 'wireless charging', intent: 'preparation-format', volume: generateVolume(3000) },
      { text: 'USB-C', intent: 'preparation-format', volume: generateVolume(5000) },
      { text: '3.5mm jack', intent: 'preparation-format', volume: generateVolume(2000) },
      { text: 'aux', intent: 'preparation-format', volume: generateVolume(1500) },
      { text: 'cable', intent: 'preparation-format', volume: generateVolume(8000) },
      
      // Attributes & Ingredients
      { text: 'over-ear', intent: 'attributes-ingredients', volume: generateVolume(5000) },
      { text: 'on-ear', intent: 'attributes-ingredients', volume: generateVolume(3000) },
      { text: 'in-ear', intent: 'attributes-ingredients', volume: generateVolume(7000) },
      { text: 'microphone', intent: 'attributes-ingredients', volume: generateVolume(4000) },
      { text: 'stereo', intent: 'attributes-ingredients', volume: generateVolume(6000) },
      { text: 'surround sound', intent: 'attributes-ingredients', volume: generateVolume(2000) },
      { text: 'active noise cancellation', intent: 'attributes-ingredients', volume: generateVolume(1500) },
      { text: 'ANC', intent: 'attributes-ingredients', volume: generateVolume(1000) },
      { text: 'transparency mode', intent: 'attributes-ingredients', volume: generateVolume(800) },
      { text: 'ambient sound', intent: 'attributes-ingredients', volume: generateVolume(600) },
      
      // Dietary / Health Claims
      { text: 'sweat resistant', intent: 'dietary-health', volume: generateVolume(2500) },
      { text: 'waterproof', intent: 'dietary-health', volume: generateVolume(3000) },
      { text: 'hypoallergenic', intent: 'dietary-health', volume: generateVolume(800) },
      
      // Occasion
      { text: 'travel', intent: 'occasion', volume: generateVolume(12000) },
      { text: 'commute', intent: 'occasion', volume: generateVolume(4000) },
      { text: 'office', intent: 'occasion', volume: generateVolume(8000) },
      { text: 'home', intent: 'occasion', volume: generateVolume(10000) },
      { text: 'workout', intent: 'occasion', volume: generateVolume(9000) },
      { text: 'exercise', intent: 'occasion', volume: generateVolume(7000) },
      { text: 'running', intent: 'occasion', volume: generateVolume(6000) },
      { text: 'jogging', intent: 'occasion', volume: generateVolume(3000) },
      { text: 'gym', intent: 'occasion', volume: generateVolume(5000) },
      { text: 'fitness', intent: 'occasion', volume: generateVolume(8000) },
      { text: 'conference', intent: 'occasion', volume: generateVolume(2000) },
      { text: 'meeting', intent: 'occasion', volume: generateVolume(3000) },
      
      // Audience
      { text: 'professional', intent: 'audience', volume: generateVolume(6000) },
      { text: 'audiophile', intent: 'audience', volume: generateVolume(1500) },
      { text: 'gaming', intent: 'audience', volume: generateVolume(5000) },
      { text: 'sport', intent: 'audience', volume: generateVolume(4000) },
      { text: 'studio', intent: 'audience', volume: generateVolume(2000) },
      { text: 'monitor', intent: 'audience', volume: generateVolume(1000) },
      { text: 'reference', intent: 'audience', volume: generateVolume(800) },
      
      // Sensory / Benefit
      { text: 'premium', intent: 'sensory-benefit', volume: generateVolume(12000) },
      { text: 'high quality', intent: 'sensory-benefit', volume: generateVolume(10000) },
      { text: 'crystal clear', intent: 'sensory-benefit', volume: generateVolume(3000) },
      { text: 'comfortable', intent: 'sensory-benefit', volume: generateVolume(8000) },
      { text: 'bass', intent: 'sensory-benefit', volume: generateVolume(6000) },
      { text: 'treble', intent: 'sensory-benefit', volume: generateVolume(2000) },
      { text: 'sound', intent: 'sensory-benefit', volume: generateVolume(15000) },
      { text: 'music', intent: 'sensory-benefit', volume: generateVolume(18000) },
      { text: 'noise-canceling', intent: 'sensory-benefit', volume: generateVolume(4000) },
      { text: 'satisfying', intent: 'sensory-benefit', volume: generateVolume(3000) },
      { text: 'immersive', intent: 'sensory-benefit', volume: generateVolume(2000) },
      
      // Flavor / Variant
      { text: 'black', intent: 'flavor-variant', volume: generateVolume(12000) },
      { text: 'white', intent: 'flavor-variant', volume: generateVolume(10000) },
      { text: 'silver', intent: 'flavor-variant', volume: generateVolume(6000) },
      { text: 'gold', intent: 'flavor-variant', volume: generateVolume(4000) },
      { text: 'rose gold', intent: 'flavor-variant', volume: generateVolume(3000) },
      { text: 'space gray', intent: 'flavor-variant', volume: generateVolume(5000) },
      { text: 'midnight', intent: 'flavor-variant', volume: generateVolume(2000) },
      { text: 'starlight', intent: 'flavor-variant', volume: generateVolume(1500) },
      
      // Convenience / Use Case
      { text: 'portable', intent: 'convenience-use-case', volume: generateVolume(8000) },
      { text: 'mobility', intent: 'convenience-use-case', volume: generateVolume(3000) },
      { text: 'freedom', intent: 'convenience-use-case', volume: generateVolume(2000) },
      { text: 'lightweight', intent: 'convenience-use-case', volume: generateVolume(7000) },
      { text: 'compact', intent: 'convenience-use-case', volume: generateVolume(5000) },
      { text: 'foldable', intent: 'convenience-use-case', volume: generateVolume(2000) },
      { text: 'adjustable', intent: 'convenience-use-case', volume: generateVolume(4000) },
      { text: 'customizable', intent: 'convenience-use-case', volume: generateVolume(3000) },
      { text: 'personalized', intent: 'convenience-use-case', volume: generateVolume(2500) },
      { text: 'hands-free', intent: 'convenience-use-case', volume: generateVolume(6000) },
      { text: 'voice control', intent: 'convenience-use-case', volume: generateVolume(4000) },
      { text: 'touch controls', intent: 'convenience-use-case', volume: generateVolume(2000) },
      { text: 'gesture', intent: 'convenience-use-case', volume: generateVolume(1000) },
      { text: 'tap', intent: 'convenience-use-case', volume: generateVolume(800) },
      { text: 'swipe', intent: 'convenience-use-case', volume: generateVolume(600) },
      { text: 'fast charge', intent: 'convenience-use-case', volume: generateVolume(5000) },
      { text: 'quick charge', intent: 'convenience-use-case', volume: generateVolume(4000) },
      { text: 'battery', intent: 'convenience-use-case', volume: generateVolume(10000) },
      { text: 'long lasting', intent: 'convenience-use-case', volume: generateVolume(6000) },
      { text: 'durable', intent: 'convenience-use-case', volume: generateVolume(5000) },
      { text: 'reliable', intent: 'convenience-use-case', volume: generateVolume(4000) },
      { text: 'sturdy', intent: 'convenience-use-case', volume: generateVolume(3000) },
      
      // Tradition / Brand Perception
      { text: 'modern', intent: 'tradition-brand', volume: generateVolume(8000) },
      { text: 'sleek', intent: 'tradition-brand', volume: generateVolume(6000) },
      { text: 'stylish', intent: 'tradition-brand', volume: generateVolume(5000) },
      { text: 'elegant', intent: 'tradition-brand', volume: generateVolume(4000) },
      { text: 'hi-fi', intent: 'tradition-brand', volume: generateVolume(2000) },
      { text: 'lossless', intent: 'tradition-brand', volume: generateVolume(1500) },
      { text: 'voice assistant', intent: 'tradition-brand', volume: generateVolume(3000) },
      { text: 'Siri', intent: 'tradition-brand', volume: generateVolume(4000) },
      { text: 'Google Assistant', intent: 'tradition-brand', volume: generateVolume(3500) },
      { text: 'Alexa', intent: 'tradition-brand', volume: generateVolume(3000) },
      { text: 'volume', intent: 'tradition-brand', volume: generateVolume(8000) },
      { text: 'play', intent: 'tradition-brand', volume: generateVolume(12000) },
      { text: 'pause', intent: 'tradition-brand', volume: generateVolume(10000) },
      { text: 'skip', intent: 'tradition-brand', volume: generateVolume(6000) },
      { text: 'previous', intent: 'tradition-brand', volume: generateVolume(4000) },
      { text: 'next', intent: 'tradition-brand', volume: generateVolume(5000) },
      { text: 'call', intent: 'tradition-brand', volume: generateVolume(8000) },
      { text: 'answer', intent: 'tradition-brand', volume: generateVolume(6000) },
      { text: 'reject', intent: 'tradition-brand', volume: generateVolume(2000) },
      { text: 'mute', intent: 'tradition-brand', volume: generateVolume(5000) },
      { text: 'unmute', intent: 'tradition-brand', volume: generateVolume(3000) },
      { text: 'zoom', intent: 'tradition-brand', volume: generateVolume(4000) },
      { text: 'teams', intent: 'tradition-brand', volume: generateVolume(3000) },
      { text: 'skype', intent: 'tradition-brand', volume: generateVolume(2000) },
      { text: 'facetime', intent: 'tradition-brand', volume: generateVolume(2500) },
      { text: 'whatsapp', intent: 'tradition-brand', volume: generateVolume(2000) },
      { text: 'telegram', intent: 'tradition-brand', volume: generateVolume(1500) },
      { text: 'discord', intent: 'tradition-brand', volume: generateVolume(1000) },
      { text: 'slack', intent: 'tradition-brand', volume: generateVolume(800) }
    ],
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '2',
    name: 'Smart Fitness Watch',
    description: 'Advanced fitness tracking watch with heart rate monitor and GPS',
    category: 'Wearables',
    keywords: convertKeywords([
      'fitness watch', 'smartwatch', 'watch', 'smart', 'wearable', 'device',
      'water resistant', 'waterproof', 'swim proof', 'wireless', 'bluetooth', 'wifi', 'cellular', 'LTE', '4G', '5G', 'magnetic', 'dock', 'cable',
      'heart rate', 'heart rate monitor', 'HRM', 'pulse', 'blood pressure', 'oxygen', 'SpO2', 'GPS', 'altitude', 'barometer', 'compass', 'display', 'screen', 'touch', 'always on', 'brightness', 'auto', 'manual', 'night mode',
      'health', 'wellness', 'sweat resistant', 'sweatproof', 'moisture', 'humidity', 'temperature', 'weather', 'sleep tracking', 'sleep stages', 'REM', 'deep sleep', 'light sleep', 'awake', 'sleep score', 'recovery', 'stress', 'stress level', 'relaxation', 'breathing', 'meditation', 'mindfulness', 'zen',
      'workout', 'exercise', 'running', 'walking', 'cycling', 'swimming', 'hiking', 'climbing', 'yoga', 'pilates', 'strength training', 'cardio', 'HIIT', 'crossfit', 'marathon', '5k', '10k', 'half marathon', 'full marathon', 'ultra', 'trail running', 'road running', 'treadmill', 'indoor', 'outdoor', 'dive', 'pool', 'ocean', 'lake', 'river',
      'athlete', 'runner', 'cyclist', 'swimmer', 'hiker', 'climber', 'yogi', 'fitness enthusiast', 'health conscious', 'active lifestyle', 'professional', 'busy', 'tech savvy',
      'tracking', 'activity', 'steps', 'calories', 'distance', 'speed', 'pace', 'accurate', 'precise', 'reliable', 'responsive', 'smooth', 'comfortable', 'lightweight', 'durable', 'satisfying', 'motivating', 'inspiring', 'empowering',
      'black', 'white', 'silver', 'gold', 'rose gold', 'space gray', 'midnight', 'starlight', 'sport', 'classic', 'premium', 'pro', 'standard',
      'connectivity', 'sync', 'backup', 'battery life', 'charge', 'charging', 'portable', 'mobile', 'on-the-go', 'hands-free', 'quick access', 'easy', 'simple', 'convenient', 'practical', 'efficient', 'time-saving', 'effortless', 'seamless', 'intuitive',
      'notifications', 'alerts', 'calls', 'texts', 'emails', 'apps', 'smartphone', 'phone', 'customizable', 'personalized', 'themes', 'faces', 'widgets', 'complications', 'data', 'analytics', 'insights', 'progress', 'goals', 'achievements', 'badges', 'streaks'
    ], 'sensory-benefit'),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '3',
    name: 'Organic Cotton T-Shirt',
    description: 'Sustainable and comfortable organic cotton t-shirt in various colors',
    category: 'Clothing',
    keywords: convertKeywords([
      't-shirt', 'shirt', 'clothing', 'apparel', 'fashion', 'garment',
      'organic', 'cotton', 'natural', 'machine washable', 'tumble dry', 'long sleeve', 'short sleeve', 'sleeveless', 'tank top', 'camisole', 'undershirt', 'base layer',
      'cotton', 'organic cotton', 'natural fiber', 'breathable', 'moisture wicking', 'soft', 'smooth', 'gentle', 'skin friendly', 'hypoallergenic', 'non-toxic', 'chemical free',
      'sustainable', 'eco friendly', 'environmentally friendly', 'ethical', 'fair trade', 'certified', 'GOTS', 'OEKO-TEX', 'organic certified', 'allergy friendly', 'sensitive skin', 'anti-bacterial', 'anti-microbial', 'odor resistant',
      'casual', 'everyday', 'work', 'office', 'weekend', 'vacation', 'travel', 'home', 'lounge', 'relax', 'sleep', 'exercise', 'gym', 'yoga', 'outdoor', 'indoor',
      'men', 'women', 'unisex', 'kids', 'children', 'baby', 'toddler', 'adult', 'teen', 'senior', 'professional', 'student', 'parent', 'athlete', 'fitness enthusiast',
      'comfortable', 'cozy', 'soft touch', 'breathable', 'lightweight', 'durable', 'long lasting', 'fade resistant', 'shrink resistant', 'wrinkle free', 'stain resistant', 'easy clean', 'carefree', 'satisfying', 'pleasing', 'enjoyable', 'relaxing', 'soothing',
      'black', 'white', 'gray', 'grey', 'navy', 'blue', 'red', 'green', 'yellow', 'orange', 'pink', 'purple', 'brown', 'beige', 'cream', 'ivory', 'off white', 'charcoal', 'heather', 'marled', 'solid', 'striped', 'polka dot', 'graphic', 'print', 'logo',
      'basic', 'essential', 'staple', 'versatile', 'easy care', 'low maintenance', 'quick dry', 'portable', 'packable', 'travel friendly', 'space saving', 'compact', 'lightweight', 'easy', 'simple', 'convenient', 'practical', 'functional', 'efficient', 'time-saving', 'effortless', 'seamless', 'intuitive',
      'classic', 'timeless', 'traditional', 'heritage', 'vintage', 'retro', 'modern', 'contemporary', 'trendy', 'fashionable', 'stylish', 'elegant', 'sophisticated', 'premium', 'quality', 'designer', 'brand', 'trusted', 'reliable', 'proven', 'established', 'reputable', 'sizes', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'plus size', 'petite', 'tall', 'regular', 'slim', 'relaxed', 'loose', 'fitted', 'oversized', 'cropped', 'colors'
    ], 'sensory-benefit'),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '4',
    name: 'Stainless Steel Water Bottle',
    description: 'Insulated stainless steel water bottle that keeps drinks cold for 24 hours',
    category: 'Kitchen',
    keywords: convertKeywords([
      'stainless steel', 'water bottle', 'insulated', 'cold', 'durable', 'kitchen', 'drinkware',
      'hydration', 'water', 'beverage', 'drink', 'liquid', 'fluid', 'thirst', 'quench',
      'refreshing', 'cool', 'ice cold', 'chilled', 'frozen', 'freeze', 'ice', 'cubes',
      'temperature', 'thermal', 'vacuum', 'double wall', 'triple wall', 'insulation',
      'hot', 'warm', 'steaming', 'coffee', 'tea', 'soup', 'broth', 'hot chocolate', 'cocoa',
      'capacity', 'volume', 'size', 'ounces', 'ml', 'liters', 'pints', 'quarts', 'gallons',
      '12oz', '16oz', '20oz', '24oz', '32oz', '40oz', '64oz', '1L', '1.5L', '2L',
      'portable', 'travel', 'on the go', 'commute', 'office', 'gym', 'workout', 'sports',
      'hiking', 'camping', 'outdoor', 'adventure', 'backpacking', 'climbing', 'running',
      'cycling', 'walking', 'jogging', 'fitness', 'exercise', 'training', 'marathon',
      'leak proof', 'spill proof', 'drip free', 'secure', 'tight', 'sealed', 'closure',
      'lid', 'cap', 'top', 'mouth', 'opening', 'spout', 'straw', 'sip', 'drink',
      'easy open', 'one hand', 'push button', 'flip top', 'screw top', 'twist', 'lock',
      'carrying', 'handle', 'loop', 'strap', 'clip', 'carabiner', 'hook', 'attachment',
      'dishwasher safe', 'easy clean', 'maintenance', 'care', 'wash', 'rinse', 'sanitize',
      'BPA free', 'non-toxic', 'safe', 'healthy', 'pure', 'clean', 'fresh', 'taste free',
      'odor free', 'stainless', 'steel', 'metal', 'aluminum', 'titanium', 'copper',
      'rust proof', 'corrosion resistant', 'scratch resistant', 'dent resistant', 'impact'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '5',
    name: 'Running Shoes',
    description: 'Lightweight running shoes with advanced cushioning technology',
    category: 'Footwear',
    keywords: convertKeywords([
      'running', 'shoes', 'lightweight', 'cushioning', 'athletic', 'comfortable', 'footwear',
      'sneakers', 'trainers', 'jogging', 'marathon', '5k', '10k', 'half marathon', 'full marathon',
      'ultra', 'trail', 'road', 'track', 'treadmill', 'indoor', 'outdoor', 'fitness', 'exercise',
      'workout', 'training', 'sports', 'athletics', 'performance', 'speed', 'endurance',
      'cushioning', 'cushion', 'padding', 'support', 'stability', 'stabilization', 'motion control',
      'pronation', 'supination', 'neutral', 'arch', 'arch support', 'high arch', 'flat foot',
      'wide', 'narrow', 'regular', 'sizes', '6', '7', '8', '9', '10', '11', '12', '13', '14',
      'men', 'women', 'unisex', 'kids', 'children', 'youth', 'toddler', 'infant', 'baby',
      'colors', 'black', 'white', 'gray', 'grey', 'navy', 'blue', 'red', 'green', 'yellow',
      'orange', 'pink', 'purple', 'brown', 'beige', 'cream', 'ivory', 'multi', 'colorful',
      'breathable', 'mesh', 'ventilation', 'airflow', 'cooling', 'temperature', 'moisture',
      'wicking', 'dry', 'sweat', 'perspiration', 'odor', 'anti-bacterial', 'anti-microbial',
      'flexible', 'flex', 'bend', 'twist', 'torsion', 'movement', 'natural', 'gait',
      'heel', 'toe', 'forefoot', 'midfoot', 'outsole', 'insole', 'midsole', 'upper',
      'sole', 'tread', 'grip', 'traction', 'slip resistant', 'non-slip', 'durable',
      'long lasting', 'wear resistant', 'abrasion', 'scuff', 'scratch', 'tear', 'rip',
      'waterproof', 'water resistant', 'weather', 'rain', 'wet', 'dry', 'mud', 'dirt',
      'clean', 'washable', 'easy care', 'maintenance', 'replaceable', 'removable', 'interchangeable'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '6',
    name: 'Wireless Phone Charger',
    description: 'Fast wireless charging pad with LED indicator and safety features',
    category: 'Electronics',
    keywords: convertKeywords([
      'wireless', 'charger', 'charging', 'pad', 'phone', 'smartphone', 'iPhone', 'Android',
      'Samsung', 'Google', 'Pixel', 'fast', 'quick', 'rapid', 'speed', 'efficient', 'power',
      'battery', 'juice', 'energy', 'electricity', 'electrical', 'cordless', 'cable free',
      'Qi', 'compatible', 'universal', 'standard', 'certified', 'safe', 'protection',
      'overcharge', 'overheating', 'short circuit', 'surge', 'voltage', 'current', 'amp',
      'watt', 'power', 'input', 'output', 'USB', 'USB-C', 'Lightning', 'micro USB',
      'adapter', 'plug', 'wall', 'outlet', 'socket', 'power strip', 'surge protector',
      'LED', 'indicator', 'light', 'glow', 'illumination', 'status', 'charging', 'charged',
      'complete', 'ready', 'notification', 'alert', 'beep', 'sound', 'vibration',
      'design', 'sleek', 'modern', 'minimalist', 'elegant', 'stylish', 'fashionable',
      'colors', 'black', 'white', 'silver', 'gold', 'rose gold', 'space gray', 'midnight',
      'material', 'plastic', 'metal', 'aluminum', 'steel', 'glass', 'ceramic', 'silicone',
      'rubber', 'leather', 'fabric', 'textile', 'premium', 'quality', 'durable', 'sturdy',
      'portable', 'travel', 'desk', 'office', 'home', 'bedroom', 'nightstand', 'car',
      'vehicle', 'automotive', 'dashboard', 'console', 'mount', 'stand', 'holder',
      'angle', 'adjustable', 'tilt', 'swivel', 'rotate', 'position', 'orientation',
      'thickness', 'thin', 'slim', 'compact', 'small', 'mini', 'pocket', 'purse', 'bag'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '7',
    name: 'Yoga Mat',
    description: 'Non-slip eco-friendly yoga mat with carrying strap',
    category: 'Fitness',
    keywords: convertKeywords([
      'yoga', 'mat', 'exercise', 'fitness', 'workout', 'stretching', 'flexibility', 'balance',
      'meditation', 'mindfulness', 'zen', 'relaxation', 'stress relief', 'wellness', 'health',
      'non-slip', 'grip', 'traction', 'sticky', 'secure', 'stable', 'firm', 'solid',
      'eco-friendly', 'environmental', 'sustainable', 'green', 'natural', 'organic', 'biodegradable',
      'recycled', 'recyclable', 'non-toxic', 'safe', 'healthy', 'chemical free', 'PVC free',
      'latex free', 'allergen free', 'hypoallergenic', 'sensitive skin', 'odor free',
      'thickness', 'thin', 'thick', 'cushioning', 'padding', 'comfort', 'comfortable',
      'soft', 'cushy', 'plush', 'supportive', 'joint protection', 'knee', 'elbow', 'wrist',
      'size', 'length', 'width', 'dimensions', 'inches', 'centimeters', 'standard', 'extra long',
      'wide', 'narrow', 'compact', 'travel', 'portable', 'lightweight', 'light', 'heavy',
      'weight', 'pounds', 'kilograms', 'carrying', 'strap', 'handle', 'loop', 'bag', 'case',
      'storage', 'roll', 'fold', 'compact', 'space saving', 'easy storage', 'convenient',
      'colors', 'purple', 'pink', 'blue', 'green', 'red', 'orange', 'yellow', 'black',
      'white', 'gray', 'grey', 'brown', 'beige', 'cream', 'ivory', 'pattern', 'design',
      'texture', 'surface', 'smooth', 'rough', 'ribbed', 'grooved', 'channels', 'lines',
      'cleaning', 'maintenance', 'care', 'wash', 'wipe', 'clean', 'sanitize', 'disinfect',
      'dry', 'air dry', 'towel dry', 'quick dry', 'fast drying', 'moisture resistant'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '8',
    name: 'Bluetooth Speaker',
    description: 'Portable waterproof Bluetooth speaker with 360-degree sound',
    category: 'Electronics',
    keywords: convertKeywords([
      'bluetooth', 'speaker', 'portable', 'wireless', 'audio', 'sound', 'music', 'stereo',
      '360', 'degree', 'surround', 'omnidirectional', 'directional', 'bass', 'treble',
      'high', 'quality', 'crystal', 'clear', 'loud', 'volume', 'amplified', 'powerful',
      'waterproof', 'water resistant', 'splash proof', 'rain', 'pool', 'beach', 'outdoor',
      'indoor', 'home', 'office', 'party', 'gathering', 'event', 'celebration', 'festival',
      'camping', 'hiking', 'backpacking', 'travel', 'vacation', 'trip', 'journey',
      'battery', 'life', 'hours', 'charge', 'charging', 'USB', 'cable', 'wireless',
      'fast', 'quick', 'rapid', 'efficient', 'power', 'energy', 'juice', 'cordless',
      'connectivity', 'pairing', 'sync', 'connection', 'range', 'distance', 'feet',
      'meters', 'stable', 'reliable', 'consistent', 'interference', 'free', 'clear',
      'controls', 'buttons', 'touch', 'gesture', 'voice', 'assistant', 'Siri', 'Google',
      'Alexa', 'hands-free', 'remote', 'app', 'smartphone', 'phone', 'tablet', 'laptop',
      'computer', 'device', 'gadget', 'tech', 'technology', 'electronic', 'digital',
      'design', 'sleek', 'modern', 'stylish', 'elegant', 'fashionable', 'trendy', 'cool',
      'colors', 'black', 'white', 'silver', 'gold', 'rose gold', 'space gray', 'midnight',
      'blue', 'red', 'green', 'yellow', 'orange', 'pink', 'purple', 'multi', 'colorful',
      'size', 'small', 'medium', 'large', 'mini', 'compact', 'pocket', 'handheld', 'desktop',
      'material', 'plastic', 'metal', 'aluminum', 'steel', 'rubber', 'silicone', 'fabric',
      'durable', 'sturdy', 'robust', 'tough', 'resistant', 'shock', 'drop', 'impact'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '9',
    name: 'Coffee Maker',
    description: 'Programmable drip coffee maker with thermal carafe and auto shut-off',
    category: 'Kitchen',
    keywords: convertKeywords([
      'coffee', 'maker', 'brewer', 'machine', 'drip', 'programmable', 'automatic', 'thermal',
      'carafe', 'pot', 'jug', 'pitcher', 'container', 'vessel', 'reservoir', 'tank',
      'water', 'capacity', 'cups', 'ounces', 'liters', 'quarts', 'gallons', 'size',
      'large', 'small', 'medium', 'family', 'single', 'serve', 'serving', 'portion',
      'grind', 'ground', 'beans', 'whole', 'fresh', 'roasted', 'dark', 'light', 'medium',
      'blend', 'single origin', 'organic', 'fair trade', 'premium', 'quality', 'gourmet',
      'filter', 'paper', 'reusable', 'permanent', 'mesh', 'basket', 'cone', 'flat',
      'basket', 'holder', 'drip', 'shower', 'head', 'spray', 'distribution', 'even',
      'temperature', 'hot', 'warm', 'heat', 'heating', 'element', 'coil', 'thermostat',
      'control', 'adjustable', 'settings', 'program', 'timer', 'clock', 'delay', 'start',
      'auto', 'shut', 'off', 'safety', 'protection', 'overheating', 'burn', 'dry',
      'boil', 'protection', 'indicator', 'light', 'LED', 'display', 'screen', 'digital',
      'analog', 'manual', 'button', 'switch', 'knob', 'dial', 'control', 'panel',
      'cleaning', 'maintenance', 'care', 'wash', 'rinse', 'descale', 'lime', 'scale',
      'removable', 'parts', 'dishwasher', 'safe', 'easy', 'clean', 'convenient',
      'design', 'sleek', 'modern', 'compact', 'space', 'saving', 'counter', 'top',
      'kitchen', 'appliance', 'countertop', 'portable', 'travel', 'office', 'home'
    ]),
    selectedKeywords: [],
    status: 'pending',
  },
  {
    id: '10',
    name: 'Laptop Stand',
    description: 'Adjustable aluminum laptop stand with cooling fan and cable management',
    category: 'Office',
    keywords: convertKeywords([
      'laptop', 'stand', 'riser', 'elevator', 'holder', 'mount', 'support', 'base',
      'adjustable', 'height', 'angle', 'tilt', 'swivel', 'rotate', 'position', 'orientation',
      'aluminum', 'metal', 'steel', 'plastic', 'wood', 'bamboo', 'material', 'construction',
      'cooling', 'fan', 'ventilation', 'airflow', 'heat', 'dissipation', 'temperature',
      'overheating', 'protection', 'performance', 'speed', 'efficiency', 'longevity',
      'cable', 'management', 'organization', 'tidy', 'neat', 'clean', 'routing', 'holder',
      'clip', 'channel', 'groove', 'slot', 'opening', 'hole', 'pass-through', 'access',
      'ergonomic', 'comfort', 'comfortable', 'posture', 'neck', 'back', 'shoulder', 'wrist',
      'health', 'wellness', 'strain', 'injury', 'prevention', 'relief', 'support',
      'portable', 'travel', 'office', 'home', 'desk', 'table', 'surface', 'workstation',
      'foldable', 'collapsible', 'compact', 'space', 'saving', 'storage', 'carrying',
      'bag', 'case', 'pouch', 'sleeve', 'protection', 'cover', 'sleeve', 'pocket',
      'size', 'dimensions', 'inches', 'centimeters', 'width', 'length', 'height', 'depth',
      'weight', 'pounds', 'kilograms', 'lightweight', 'heavy', 'sturdy', 'stable',
      'compatibility', 'laptop', 'notebook', 'computer', 'MacBook', 'Dell', 'HP', 'Lenovo',
      'Asus', 'Acer', 'Samsung', 'Microsoft', 'Surface', 'size', 'screen', 'inch', 'diagonal'
    ]),
    selectedKeywords: [],
    status: 'pending',
  }
];

export const sampleProjects: Project[] = [
  {
    id: '1',
    name: 'TechCorp Q1 Product Launch',
    description: 'Keyword optimization for new tech products launching in Q1',
    brand: sampleBrands[0],
    products: sampleProducts.slice(0, 5), // 5 products
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-20'),
    status: 'active',
    totalProducts: 5,
    completedProducts: 0,
  },
  {
    id: '2',
    name: 'FashionForward Spring Collection',
    description: 'Keyword strategy for spring fashion line including clothing and accessories',
    brand: sampleBrands[1],
    products: sampleProducts.slice(5, 10), // 5 products
    createdAt: new Date('2024-01-10'),
    updatedAt: new Date('2024-01-18'),
    status: 'active',
    totalProducts: 5,
    completedProducts: 0,
  },
  {
    id: '3',
    name: 'HomeEssentials Kitchen Products',
    description: 'Keywords for kitchen and home improvement products',
    brand: sampleBrands[2],
    products: [sampleProducts[3], sampleProducts[8]], // 2 products for now
    createdAt: new Date('2024-01-05'),
    updatedAt: new Date('2024-01-15'),
    status: 'draft',
    totalProducts: 2,
    completedProducts: 0,
  },
  {
    id: '4',
    name: 'SportsPro Athletic Gear',
    description: 'Keyword optimization for sports and fitness equipment',
    brand: sampleBrands[3],
    products: [sampleProducts[4], sampleProducts[6]], // 2 products for now
    createdAt: new Date('2024-01-12'),
    updatedAt: new Date('2024-01-19'),
    status: 'active',
    totalProducts: 2,
    completedProducts: 0,
  },
];
